import type { Dispatch, SetStateAction } from "react";

import { Button, Column } from "@hightouch/htui";

import type { ColumnOption } from "src/formkit/components/formkit-context";
import { ArrayInput } from "src/formkit/components/mapper-v2/array-input";
import { ArrayPropertiesInput } from "src/formkit/components/mapper-v2/array-properties-input";
import { MapperPaneFooter } from "src/formkit/components/mapper-v2/mapper-list";
import type { MapperValue } from "src/formkit/components/mapper-v2/mapper-selection";
import { ObjectInput } from "src/formkit/components/mapper-v2/object-input";
import { TemplateInput } from "src/formkit/components/mapper-v2/template-input";
import {
  type JsonColumnProps,
  MappingType,
  type OverwriteStandardInputWithArrayProps,
  type TemplateInfo,
} from "src/formkit/components/types";

type LegacyPaneProps = {
  /** The mapping type whose pane to show. */
  type: string;
  draft: MapperValue;
  columnOptions: ColumnOption[] | undefined;
  overwriteColumnsWithArrayProps: boolean | undefined;
  jsonColumnProperties: JsonColumnProps;
  onChangeJsonColumnProperties: Dispatch<SetStateAction<JsonColumnProps>>;
  onReloadEligibleInlineMapperColumns: (currentSelectedColumn?: string) => void;
  templates: TemplateInfo[];
  /** Receives the array row pane's search box, the popover's initial focus target. */
  focusRef: (element: HTMLElement | null) => void;
  onDraftChange: (draft: MapperValue) => void;
  onApply: (value: MapperValue) => void;
  onClose: () => void;
};

/** The legacy panes type a value's `type` with string literals rather than `MappingType`. */
type LegacyPaneValue = Omit<MapperValue, "type"> & { type?: string };

/**
 * Shows a value type's pane from the legacy mapper inside the redesigned
 * popover, wired the way the legacy mapper wires it.
 */
export function LegacyPane({
  type,
  draft,
  columnOptions,
  overwriteColumnsWithArrayProps,
  jsonColumnProperties,
  onChangeJsonColumnProperties,
  onReloadEligibleInlineMapperColumns,
  templates,
  focusRef,
  onDraftChange,
  onApply,
  onClose,
}: LegacyPaneProps) {
  const arrayProps: OverwriteStandardInputWithArrayProps =
    overwriteColumnsWithArrayProps
      ? { overwriteColumnsWithArrayProps: true, columnOptions }
      : { columnOptions };
  const formProps = {
    ...arrayProps,
    onReloadEligibleInlineMapperColumns,
    templates,
    value: draft,
  };
  // Panes that commit on choice pass the close callback first.
  const applyChoice = (_close: unknown, value?: LegacyPaneValue) => {
    if (value) onApply(toMapperValue(value));
  };
  // Array element properties are named by strings.
  const arrayPropertyOptions = (columnOptions ?? []).flatMap((option) =>
    typeof option.value === "string"
      ? [{ ...option, value: option.value }]
      : [],
  );
  const editsDraft =
    type === MappingType.TEMPLATE || type === MappingType.OBJECT;

  const renderInput = () => {
    switch (type) {
      case MappingType.TEMPLATE:
        return (
          <TemplateInput
            {...formProps}
            jsonColumnProperties={jsonColumnProperties}
            onChange={onDraftChange}
          />
        );
      case MappingType.OBJECT:
        return <ObjectInput {...formProps} onChange={onDraftChange} />;
      case MappingType.ARRAY:
        return (
          <ArrayInput
            {...formProps}
            jsonColumnProperties={jsonColumnProperties}
            onChange={applyChoice}
            onChangeJsonColumnProperties={onChangeJsonColumnProperties}
            onClose={onClose}
          />
        );
      default:
        // Inside an array row, a column selection picks one of the array's properties.
        return (
          <ArrayPropertiesInput
            {...formProps}
            ref={focusRef}
            columnOptions={arrayPropertyOptions}
            jsonColumnProperties={jsonColumnProperties}
            overwriteColumnsWithArrayProps
            onChange={applyChoice}
            onClose={onClose}
          />
        );
    }
  };

  return (
    <>
      <Column flex={1} minHeight={0} pt={3}>
        {renderInput()}
      </Column>
      {editsDraft && (
        <MapperPaneFooter>
          <Button variant="primary" onClick={() => onApply(draft)}>
            Apply
          </Button>
        </MapperPaneFooter>
      )}
    </>
  );
}

function toMapperValue(value: LegacyPaneValue): MapperValue {
  return {
    ...value,
    type: Object.values(MappingType).find(
      (mappingType) => mappingType === value.type,
    ),
  };
}
